import { useMutation, useQuery } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';
import { ILegalTransactionGrundbuchblattLink } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';

//////////////////// Grundbuchblatt-Suche (Übersicht)
export interface IGrundbuchblattHit {
  idGrundbuchblatt: number;
  Blattnummer: string;
  idCourt?: number | null;
  idAdministrativeUnit?: number | null;
  Amtsgericht?: string | null;
  Gemarkung?: string | null;
  FlurstueckAnzahl?: number | null;
  Adressen?: string | null;
  Eigentuemer?: string | null;
}

/**
 * Suche über Blattnummer, Gemarkung, Flur, Flurstück, Adresse, Eigentümer.
 * Erst ab 2 Zeichen aktiv (Backend: /grundbuchblatt/search).
 */
export const useSearchGrundbuchblatt = (q?: string) => {
  const { data, isFetching } = useQuery<IGrundbuchblattHit[]>({
    // Immer laden: ohne Suchbegriff liefert das Backend alle Blätter
    // (automatische Tabelle); mit Begriff wird ergänzend gefiltert.
    enabled: true,
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    queryKey: ['grundbuchblatt', 'search', (q ?? '').toLowerCase()],
    queryFn: () =>
      customFetcher({
        endpoint: `/grundbuchblatt/search?q=${encodeURIComponent(q ?? '')}`,
      })
        .then(r => (Array.isArray(r) ? (r as IGrundbuchblattHit[]) : []))
        .catch(() => [] as IGrundbuchblattHit[]),
  });
  return {
    grundbuchblattHits: Array.isArray(data) ? data : [],
    isSearchingGrundbuchblatt: isFetching,
  };
};

//////////////////// Grundbuchblatt-Detail
export interface IGrundbuchblattFlurstueck {
  idRealEstateLandRegisterEntry: number;
  LandRegister?: string | null;
  Flur?: string | null;
  Flurstueck?: string | null;
  idRealEstate?: number | null;
  Latitude?: number | null;
  Longitude?: number | null;
  Street?: string | null;
  StreetNr?: string | null;
  ZipCode?: string | null;
  Place?: string | null;
}

export interface IGrundbuchblattOwner {
  idLegalClient: number;
  FirstName?: string | null;
  LastName?: string | null;
  idLegalClientType?: number | null;
  share?: number | null;
  idGrundstueck?: number | null;
  GrundstueckLfdNr?: string | null;
  WeitereBlaetter?: number | null;
  // Abt. I 1:1 (read)
  lfdNr?: string | null;
  GrundlageDerEintragung?: string | null;
  Gemeinschaftsverhaeltnis?: string | null;
  AnteilText?: string | null;
  eingetragenAm?: string | null;
  Rechtspfleger?: string | null;
}

export interface IGrundbuchblattAbruf {
  idRealEstateLandRegisterEntryQuery: number;
  idGrundbuchblattAbruf?: number;
  idRealEstateLandRegisterEntry?: number | null;
  idDocument?: number | null;
  FileName?: string | null;
  ListOfPending?: string | null;
  ActualityProof?: string | null;
  Created_at?: string | null;
}

export interface IGrundbuchblattDetail {
  idGrundbuchblatt: number;
  Blattnummer: string;
  Blattart?: string | null;
  Aufschriftvermerke?: string | null;
  Amtsgericht?: string | null;
  Gemarkung?: string | null;
  GemarkungFreitext?: string | null;
  idCourt?: number | null;
  idAdministrativeUnit?: number | null;
  Flurstuecke: IGrundbuchblattFlurstueck[];
  GrundstueckArray?: IBlattGrundstueck[];
  Eigentuemer: IGrundbuchblattOwner[];
  Abrufe: IGrundbuchblattAbruf[];
  fehler?: string;
}

export const useGrundbuchblattDetail = (idGrundbuchblatt?: number) => {
  const { data, isLoading, refetch } = useQuery<IGrundbuchblattDetail>({
    enabled: !!idGrundbuchblatt && idGrundbuchblatt > 0,
    refetchOnWindowFocus: false,
    queryKey: ['grundbuchblatt', 'detail', idGrundbuchblatt],
    queryFn: () =>
      customFetcher({ endpoint: `/grundbuchblatt/${idGrundbuchblatt}/detail` }),
  });
  return { detail: data, isLoadingDetail: isLoading, refetchDetail: refetch };
};

//////////////////// Eigentümer-Sicht (wiederverwendbar)
export interface IOwnerBlatt {
  idGrundbuchblatt: number;
  Blattnummer: string;
  share?: number | null;
  idCourt?: number | null;
  idAdministrativeUnit?: number | null;
  Amtsgericht?: string | null;
  Gemarkung?: string | null;
  FlurstueckAnzahl?: number | null;
}

export interface IOwnerGrundstueck {
  idRealEstateLandRegisterEntry: number;
  idGrundbuchblatt?: number | null;
  Blattnummer?: string | null;
  LandRegister?: string | null;
  Flur?: string | null;
  Flurstueck?: string | null;
  idRealEstate?: number | null;
  Latitude?: number | null;
  Longitude?: number | null;
  Street?: string | null;
  StreetNr?: string | null;
  ZipCode?: string | null;
  Place?: string | null;
}

export interface IOwnerDetail {
  idLegalClient: number;
  FirstName?: string | null;
  LastName?: string | null;
  SurName?: string | null;
  idLegalClientType?: number | null;
  Email?: string | null;
  Blaetter: IOwnerBlatt[];
  Grundstuecke: IOwnerGrundstueck[];
  fehler?: string;
}

//////////////////// Editierbares Blatt-fullObject (Blatt-zentrischer Umbau)
export interface IBlattAddress {
  idGrundbuchblatt_has_RealEstate?: number;
  idRealEstate: number;
  idAddress?: number | null;
  Street?: string | null;
  StreetNr?: string | null;
  ZipCode?: string | null;
  Place?: string | null;
  Latitude?: number | null;
  Longitude?: number | null;
}

export interface IBlattFlurstueck {
  idRealEstateLandRegisterEntry: number; // <0 = neu
  LandRegister?: string | null; // Gemarkung-Text am Eintrag
  Parcel?: string | null; // Flur
  LandParcel?: string | null; // Flurstück
  SheetLandRegister?: string | null; // Blattnummer am Eintrag
  idCourt?: number | null;
  idAdministrativeUnit?: number | null;
  idRealEstate?: number | null;
  Latitude?: number | null;
  Longitude?: number | null;
}

export interface IBlattOwner {
  idLegalClient: number;
  idLegalClientAssetRole?: number | null;
  share?: number | null;
  idGrundstueck?: number | null; // Grundstücks-Referenz (lfd. Nr.); null = ganzes Blatt
  FirstName?: string | null;
  LastName?: string | null;
  SurName?: string | null;
  idLegalClientType?: number | null;
  // Abt. I 1:1: lfd. Nr., Grundlage der Eintragung, Gemeinschaftsverhältnis, Historisierung
  lfdNr?: string | null;
  GrundlageDerEintragung?: string | null;
  Gemeinschaftsverhaeltnis?: string | null;
  AnteilText?: string | null;
  eingetragenAm?: string | null;
  istGeloescht?: number | boolean | null;
  geloeschtAm?: string | null;
  Rechtspfleger?: string | null;
}

/** Gekoppelte Zeile: optional Flur/Flurstück UND optional Adresse zusammen. */
export interface IBlattRow {
  idRealEstateLandRegisterEntry?: number | null; // Flurstück, <0/null = neu
  Parcel?: string | null; // Flur
  LandParcel?: string | null; // Flurstück
  LandRegister?: string | null;
  idRealEstate?: number | null; // Adresse, <0/null = neu
  Street?: string | null;
  StreetNr?: string | null;
  idPostcode?: number | null;
  ZipCode?: string | null;
  Place?: string | null;
  Latitude?: number | null;
  Longitude?: number | null;
}

//////////////////// Bestandsverzeichnis (neue Kette: Grundstueck -> Flurstueck/Adresse)
/** Flurstück unter einem Grundstück (neue Flurstueck-Tabelle). */
export interface IBlattFlurstueckNeu {
  idFlurstueck?: number | null; // <0/null = neu
  Flur?: string | null; // Sachsen: leer
  Flurstuecksnummer?: string | null;
  idAdministrativeUnit?: number | null; // Gemarkung
  Gemarkung?: string | null; // Anzeigetext (read)
  Wirtschaftsart?: string | null;
  Lage?: string | null;
  Groesse?: number | null;
}

/** Adresse direkt am Grundstück (M:N über Grundstueck_has_Address). */
export interface IGrundstueckAddress {
  idGrundstueck_has_Address?: number | null;
  idAddress?: number | null; // <0/null = neu
  Street?: string | null;
  StreetNr?: string | null;
  StreetAdditional?: string | null;
  idPostcode?: number | null;
  ZipCode?: string | null; // Anzeigetext (read)
  Place?: string | null; // Anzeigetext (read)
}

/** Abteilung II: Last/Beschränkung an einem Grundstück. */
export interface IBlattAbteilung2Entry {
  idGrundbuchAbteilung2?: number | null; // <0/null = neu
  idGrundstueck?: number | null; // Referenz auf das betroffene Grundstück (lfd. Nr.)
  lfdNr?: string | null;
  Belastungsart?: string | null;
  Berechtigter?: string | null;
  Inhalt?: string | null;
  Betrag?: number | null;
  Waehrung?: string | null;
  bewilligtAm?: string | null;
  eingetragenAm?: string | null;
  GrundlageDerEintragung?: string | null;
  isVormerkung?: number | boolean | null;
  // 1:1: wörtlicher Text, Rang, Gesamtrechte, Veränderungen, Historisierung
  Volltext?: string | null;
  Rangvermerk?: string | null;
  GesamthaftBlaetter?: string | null;
  Veraenderungen?: string | null;
  istGeloescht?: number | boolean | null;
  geloeschtAm?: string | null;
  Rechtspfleger?: string | null;
}

/** Abteilung III: Grundpfandrecht an einem Grundstück. */
export interface IBlattAbteilung3Entry {
  idGrundbuchAbteilung3?: number | null; // <0/null = neu
  idGrundstueck?: number | null; // Referenz auf das betroffene Grundstück (lfd. Nr.)
  lfdNr?: string | null;
  Belastungsart?: string | null;
  Betrag?: number | null;
  Waehrung?: string | null;
  Glaeubiger?: string | null;
  Zinssatz?: number | null;
  Inhalt?: string | null;
  bewilligtAm?: string | null;
  eingetragenAm?: string | null;
  GrundlageDerEintragung?: string | null;
  isOhneBrief?: number | boolean | null;
  isVollstreckbar?: number | boolean | null;
  // 1:1: wörtlicher Text, Rang, Gesamtrechte, Nebenleistung, Veränderungen, Historisierung
  Volltext?: string | null;
  Rangvermerk?: string | null;
  GesamthaftBlaetter?: string | null;
  Nebenleistung?: string | null;
  Veraenderungen?: string | null;
  istGeloescht?: number | boolean | null;
  geloeschtAm?: string | null;
  Rechtspfleger?: string | null;
}

/** Bestandsverzeichnis-Position (Grundstück) mit lfd. Nr. + Flurstücken/Adressen/Abteilungen. */
export interface IBlattGrundstueck {
  idGrundstueck?: number | null; // <0/null = neu
  lfdNr?: string | null;
  bisherigeLfdNr?: string | null;
  // Wohnungs-/Teileigentum (#1) + herrschende Rechte (#8) + Bestandsvermerke (#7)
  istWohnungseigentum?: number | boolean | null;
  MiteigentumsanteilZaehler?: number | null;
  MiteigentumsanteilNenner?: number | null;
  SondereigentumText?: string | null;
  AufteilungsplanNr?: string | null;
  TeilungserklaerungBezug?: string | null;
  Veraeusserungsbeschraenkung?: string | null;
  HerrschendeRechte?: string | null;
  // Bestandsverzeichnis Spalte 6 (Bestand und Zuschreibungen) + Spalte 8
  // (Abschreibungen) — je mit eigenem unterzeichnenden Rechtspfleger.
  Bestandsvermerke?: string | null;
  BestandsvermerkeRechtspfleger?: string | null;
  Abschreibungen?: string | null;
  AbschreibungenRechtspfleger?: string | null;
  // Erbbaurecht (#1158) — analog zu Wohnungs-/Teileigentum.
  istErbbaurecht?: number | boolean | null;
  ErbbauzinsText?: string | null;
  ErbbaurechtLaufzeitBis?: string | null;
  ErbbaurechtBezug?: string | null;
  FlurstueckArray: IBlattFlurstueckNeu[];
  AddressArray: IGrundstueckAddress[];
  Abteilung2Array?: IBlattAbteilung2Entry[];
  Abteilung3Array?: IBlattAbteilung3Entry[];
}

export interface IGrundbuchblattFullObject {
  idGrundbuchblatt: number; // <0 = neu
  Blattnummer: string;
  Blattart?: string | null;
  Aufschriftvermerke?: string | null;
  GemarkungFreitext?: string | null;
  idCourt?: number | null;
  idAdministrativeUnit?: number | null;
  Amtsgericht?: string | null;
  Gemarkung?: string | null;
  GrundstueckArray: IBlattGrundstueck[];
  AddressArray: IBlattAddress[];
  FlurstueckArray: IBlattFlurstueck[];
  RowArray: IBlattRow[];
  OwnerArray: IBlattOwner[];
  Abrufe: IGrundbuchblattAbruf[];
  fehler?: string;
}

/** Blatt-id zu einem Grundstück (RealEstate) ermitteln (oder null). */
export const useBlattForRealEstate = (idRealEstate?: number) => {
  const enabled = !!idRealEstate && idRealEstate > 0;
  const { data } = useQuery<{ idGrundbuchblatt: number | null }>({
    enabled,
    refetchOnWindowFocus: false,
    queryKey: ['grundbuchblatt', 'forRealEstate', idRealEstate],
    queryFn: () =>
      customFetcher({
        endpoint: `/grundbuchblatt/forRealEstate/${idRealEstate}`,
      }),
  });
  return {
    resolvedIdGrundbuchblatt: data?.idGrundbuchblatt ?? null,
    // settled = Query deaktiviert (kein Blatt möglich) ODER Antwort da
    blattResolveSettled: !enabled || data !== undefined,
  };
};

export const useGrundbuchblattFullObject = (idGrundbuchblatt?: number) => {
  const { data, isLoading, refetch } = useQuery<IGrundbuchblattFullObject>({
    enabled: !!idGrundbuchblatt && idGrundbuchblatt > 0,
    refetchOnWindowFocus: false,
    queryKey: ['grundbuchblatt', 'fullObject', idGrundbuchblatt],
    queryFn: () =>
      customFetcher({
        endpoint: `/grundbuchblatt/${idGrundbuchblatt}/fullObject`,
      }),
  });
  return {
    blattFullObject: data,
    isLoadingBlattFullObject: isLoading,
    refetchBlattFullObject: refetch,
  };
};

export const useSaveGrundbuchblattFullObject = () => {
  const { mutateAsync: saveBlatt, isPending: isSavingBlatt } = useMutation({
    mutationFn: (blatt: Partial<IGrundbuchblattFullObject>) =>
      customFetcher({
        endpoint: '/grundbuchblatt/fullObject',
        method: 'PUT',
        body: blatt,
      }) as Promise<IGrundbuchblattFullObject>,
  });
  return { saveBlatt, isSavingBlatt };
};

/** Ein Eintrag im Aktivitäts-Log eines Grundbuchblatts. */
export interface IGrundbuchblattLog {
  idGrundbuchblattLog: number;
  idGrundbuchblatt: number;
  idGrundbuchblattLogType: number;
  LogType?: string | null;
  Timestamp: string;
  Log: string;
}

/** Aktivitäts-Log eines Grundbuchblatts (neueste zuerst). */
export const useGrundbuchblattLog = (idGrundbuchblatt?: number) => {
  const { data, isLoading, refetch } = useQuery<IGrundbuchblattLog[]>({
    enabled: !!idGrundbuchblatt && idGrundbuchblatt > 0,
    refetchOnWindowFocus: false,
    queryKey: ['grundbuchblatt', 'log', idGrundbuchblatt],
    queryFn: () =>
      customFetcher({ endpoint: `/grundbuchblatt/log/${idGrundbuchblatt}` }),
  });
  return {
    grundbuchblattLog: data ?? [],
    isLoadingGrundbuchblattLog: isLoading,
    refetchGrundbuchblattLog: refetch,
  };
};

/**
 * Eigentümer-zentrierte Sicht (Backend: /grundbuchblatt/byOwner/{id}) für die
 * wiederverwendbare Eigentümer-Detail-Komponente / spätere Eigentümerrecherche.
 */
export const useGrundbuchblaetterByOwner = (idLegalClient?: number) => {
  const { data, isLoading, refetch } = useQuery<IOwnerDetail>({
    enabled: !!idLegalClient && idLegalClient > 0,
    refetchOnWindowFocus: false,
    queryKey: ['grundbuchblatt', 'byOwner', idLegalClient],
    queryFn: () =>
      customFetcher({
        endpoint: `/grundbuchblatt/byOwner/${idLegalClient}`,
      }),
  });
  return {
    ownerDetail: data,
    isLoadingOwnerDetail: isLoading,
    refetchOwnerDetail: refetch,
  };
};

///////////////////////////////////////////////////////////////////////////////
// Recherche -> Blatt: Mandanten-Match (wie Dialog-Import) + Anlage pro Blattnummer
///////////////////////////////////////////////////////////////////////////////

/** Ein bewerteter Mandanten-Treffer zu einem Eigentümer-Namen. */
export interface IRechercheClientMatch {
  idLegalClient: number;
  FirstName?: string | null;
  LastName?: string | null;
  Surname?: string | null;
  Birthdate?: string | null;
  Email?: string | null;
  score: number;
  matchedFields: string[];
}

/** Eigentümer-Entscheidung je Recherche-Eigentümer (verwenden / neu anlegen). */
export interface IRechercheOwnerInput {
  action: 'use' | 'create' | 'ignore';
  idLegalClient?: number | null;
  FirstName?: string | null;
  LastName?: string | null;
  Birthday?: string | null;
  share?: number | null;
}

/** Ein anzulegendes Blatt aus der Recherche (pro Blattnummer). */
export interface IRechercheBlattInput {
  Blattnummer: string;
  idCourt?: number | null;
  idAdministrativeUnit?: number | null;
  assignToVorgang: boolean;
  grundstuecke: IBlattGrundstueck[];
  owners: IRechercheOwnerInput[];
}

export interface IFromRecherchePayload {
  blaetter: IRechercheBlattInput[];
}

export interface IFromRechercheResultEntry {
  Blattnummer: string;
  idGrundbuchblatt?: number;
  /** Vom Aufrufer gewünschte Zuordnung (Verknüpfung erfolgt erst beim Vorgang-Save). */
  assignToVorgang?: boolean;
  ownerCount?: number;
  fehler?: string;
  /** Kompaktes Link-Objekt für die Vorgang-GrundbuchblattArray. */
  link?: ILegalTransactionGrundbuchblattLink;
}

/** Bewertete Mandanten-Treffer zu einem Eigentümer-Namen (Recherche). */
export const useRechercheClientMatch = () => {
  const { mutateAsync: matchClients, isPending: isMatchingClients } =
    useMutation({
      mutationFn: (query: {
        LastName?: string | null;
        FirstName?: string | null;
        Birthday?: string | null;
      }) =>
        customFetcher({
          endpoint: '/grundbuchblatt/recherche/clientMatch',
          method: 'PUT',
          body: query,
        }) as Promise<{ matches: IRechercheClientMatch[] }>,
    });
  return { matchClients, isMatchingClients };
};

/** Aus Recherche-Ergebnissen Grundbuchblätter anlegen (+ optional Vorgang). */
export const useFromRecherche = () => {
  const {
    mutateAsync: createBlaetterFromRecherche,
    isPending: isCreatingBlaetter,
  } = useMutation({
    mutationFn: (payload: IFromRecherchePayload) =>
      customFetcher({
        endpoint: '/grundbuchblatt/recherche/fromRecherche',
        method: 'PUT',
        body: payload,
      }) as Promise<{ created: IFromRechercheResultEntry[] }>,
  });
  return { createBlaetterFromRecherche, isCreatingBlaetter };
};
